import React, { useState, useEffect, useCallback } from 'react';

import { Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ImageUpload from 'components/ImageUpload';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledDrawer, HeadingText } from 'styles/admin';
import {
  StyledTypography,
  ColoredCircularProgress,
  StyledButton,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
  NewStyledIconContainer,
  FlexContainer,
} from 'styles/common';

import { StyledIcon } from 'constants/icons';

import { showSuccessSnackbar, showErrorSnackbar } from 'services/snackbar';

import { StyledPanelHeader, StyledDivider, StyledButtonContainer } from './styles';

const FileUploadSidePanel = ({
  open,
  onCancel,
  onSuccess,
  title,
  helperText,
  uploadHandlerService,
  uploadHandlerServiceAdditionalParams = {}, // You can pass an object of params that you want to pass in the upload handler service.
}) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) onCancel();
  };

  const validateUploadFile = (file) => {
    if (file.size > 10 * 1000 * 1000) {
      const error = 'file size cannot be more than 10 mb.';
      dispatch(showErrorSnackbar(error));
      return false;
    }
    return true;
  };

  const selectUploadFile = (file) => {
    if (validateUploadFile(file)) setSelectedFile(file);
  };

  const uploadFile = () => {
    const fileObject = selectedFile;
    setUploading(true);

    uploadHandlerService({ fileObject, ...uploadHandlerServiceAdditionalParams })
      .then((res) => {
        dispatch(showSuccessSnackbar('File upload successful.', 3000));
        onSuccess({ res });
      })
      .catch((err) => {
        console.log('upload error', err.message);
        dispatch(showErrorSnackbar(err.message, 5000));
      })
      .finally(() => setUploading(false));
  };

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setUploading(false);
  });

  useEffect(() => {
    // on close of side Panel, clear the state.
    if (!open) {
      resetState();
    }
  }, [open]);

  return (
    <StyledDrawer anchor="right" open={open} ModalProps={{ onBackdropClick: onCancel }} onKeyDown={handleKeyDown}>
      <StyledPanelHeader>
        <HeadingText>Upload Employees List</HeadingText>
        <Button size="small" variant="text" onClick={() => onCancel()}>
          <StyledIcon color="textSecondary" size="header">
            <CloseIcon />
          </StyledIcon>
          <Typography style={{ textTransform: 'none' }} color="textSecondary">
            Cancel
          </Typography>
        </Button>
      </StyledPanelHeader>
      <StyledDivider />

      <VerticalFlexContainer>
        <div>
          {title && (
            <StyledTypography weight="bold" variant="body1">
              {title}
            </StyledTypography>
          )}
          <VerticallyAlignedFlexContainer topGutter={3} bottomGutter={3}>
            <ImageUpload
              setFile={selectUploadFile}
              accept=".csv"
              icon={
                <StyledButton fontColor="primary" size="medium" variant="outlined" rightGutter={2}>
                  <StyledIcon>
                    <AddIcon />
                  </StyledIcon>
                  <StyledTypography weight="bold" variant="body1">
                    Choose File
                  </StyledTypography>
                </StyledButton>
              }
            />
            <StyledTypography color="textSecondary">
              {selectedFile === null ? 'No File Chosen' : selectedFile.name}
            </StyledTypography>
          </VerticallyAlignedFlexContainer>

          <FlexContainer style={{ alignItems: 'center' }}>
            <NewStyledIconContainer color="textSecondary" size="header" rightGutter={1}>
              <StyledInfoIcon />
            </NewStyledIconContainer>
            <StyledTypography variant="body1" color="textSecondary">
              {helperText}
            </StyledTypography>
          </FlexContainer>
        </div>

        <div>
          <StyledButtonContainer>
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              onClick={uploadFile}
              disabled={selectedFile === null}
            >
              {uploading ? (
                <ColoredCircularProgress size="tiny" />
              ) : (
                <StyledTypography color="white">Upload File</StyledTypography>
              )}
            </StyledButton>
          </StyledButtonContainer>
        </div>
      </VerticalFlexContainer>
    </StyledDrawer>
  );
};

const StyledInfoIcon = styled(InfoOutlinedIcon)`
  margin-bottom: -3px;
  margin-right: 2px;
`;

export default FileUploadSidePanel;
